import { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { AllowedEmailDomainsEnforcementLevel } from '@bringg/types';
import { getSharedRootStore } from '@bringg-frontend/global-stores';
import { isEmailDomainAllowed } from '@bringg-frontend/utils';

import { useHasFeatureFlag } from './feature-flags';

const ALLOWED_EMAIL_DOMAINS_FF = 'enable_restrict_admins_allowed_domains';

type AntdValidator = (rule: unknown, value: string) => Promise<void>;

// IMPORTANT: The enforcement and domain-matching logic below is intentionally mirrored in the AngularJS layer:
// apps/bringg-web/src/app/scripts/services/employee-email-domain-validation-service.js
// If you change the validation logic here, make sure to apply the same change there.
export function useAllowedEmailDomainValidator(
	requiredEnforcementLevel: AllowedEmailDomainsEnforcementLevel
): AntdValidator | null {
	const { t } = useTranslation();
	const isFeatureEnabled = useHasFeatureFlag(ALLOWED_EMAIL_DOMAINS_FF);
	const { allowed_email_domains: allowedDomains, allowed_email_domains_enforcement_level: enforcementLevel } =
		getSharedRootStore().data.merchantConfigurationsStore.configuration;

	const isEnforced = useMemo((): boolean => {
		if (!isFeatureEnabled) {
			return false;
		}

		if (!allowedDomains?.length) {
			return false;
		}

		const effectiveLevel = enforcementLevel ?? AllowedEmailDomainsEnforcementLevel.AdminsOnly;

		return effectiveLevel >= requiredEnforcementLevel;
	}, [allowedDomains, enforcementLevel, requiredEnforcementLevel, isFeatureEnabled]);

	const validator: AntdValidator = useCallback(
		(_, value: string) => {
			if (!value) {
				return Promise.resolve();
			}

			if (!isEmailDomainAllowed(value, allowedDomains)) {
				return Promise.reject(
					new Error(
						t('CREATE_USER_MODAL.EMAIL_DOMAIN_NOT_IN_ALLOWED_LIST', {
							domains: allowedDomains.join(', ')
						})
					)
				);
			}

			return Promise.resolve();
		},
		[allowedDomains, t]
	);

	if (!isEnforced) {
		return null;
	}

	return validator;
}
